import React, { useState, useEffect, useCallback } from "react";
import apiService from "../../ApiService";

const MonthlyCorpsForm = ({ onDataUpdate }) => {
  const [monthlyCorps, setMonthlyCorps] = useState([]);
  const [formData, setFormData] = useState({
    id: null, // track if editing
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    india_stock: "",
    india_bond: "",
    usa_stock: "",
    usa_fixed: "",
    india_pension: "",
    hsa: "",
    icici_prulife: "",
    k_401k: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const formatINR = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(val);
  };

  const formatUSD = (val) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(val);
  };

  const fetchMonthlyCorps = useCallback(async () => {
    setLoading(true);
    setError("");
    const data = await apiService.getMonthlyCorps();
    if (data) {
      setMonthlyCorps(data);
    } else {
      setError("Failed to fetch records.");
    }
    setLoading(false);
  }, []);

  // Fetch all records on mount
  useEffect(() => {
    fetchMonthlyCorps();
  }, [fetchMonthlyCorps]);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit form to create or update record
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.year || !formData.month) {
      setError("Year and month are required");
      return;
    }

    const recordPayload = {
      year: parseInt(formData.year, 10),
      month: parseInt(formData.month, 10),
      india_stock: parseFloat(formData.india_stock) || 0,
      india_bond: parseFloat(formData.india_bond) || 0,
      usa_stock: parseFloat(formData.usa_stock) || 0,
      usa_fixed: parseFloat(formData.usa_fixed) || 0,
      india_pension: parseFloat(formData.india_pension) || 0,
      hsa: parseFloat(formData.hsa) || 0,
      icici_prulife: parseFloat(formData.icici_prulife) || 0,
      k_401k: parseFloat(formData.k_401k) || 0,
    };

    let data;
    if (formData.id) {
      // Editing existing record
      data = await apiService.updateMonthlyCorp(formData.id, recordPayload);
    } else {
      // Creating new record
      data = await apiService.createMonthlyCorp(recordPayload);
    }

    if (
      data &&
      data.message &&
      !data.message.toLowerCase().includes("unable")
    ) {
      fetchMonthlyCorps();
      resetForm();
      // Notify parent component about data update
      if (onDataUpdate) onDataUpdate();
    } else {
      setError("Failed to save record");
    }
  };

  // Reset form to initial add mode
  const resetForm = () => {
    setFormData({
      id: null,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      india_stock: "",
      india_bond: "",
      usa_stock: "",
      usa_fixed: "",
      india_pension: "",
      hsa: "",
      icici_prulife: "",
      k_401k: "",
    });
    setError("");
  };

  // Load data into form for editing
  const handleEdit = (record) => {
    setFormData({
      id: record.id,
      year: record.year,
      month: record.month,
      india_stock: record.india_stock,
      india_bond: record.india_bond,
      usa_stock: record.usa_stock,
      usa_fixed: record.usa_fixed,
      india_pension: record.india_pension,
      hsa: record.hsa,
      icici_prulife: record.icici_prulife,
      k_401k: record.k_401k,
    });
    setError("");
  };

  // Delete record by id
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    const data = await apiService.deleteMonthlyCorp(id);
    if (data && data.message === "Record deleted.") {
      fetchMonthlyCorps();
      // Notify parent component about data update
      if (onDataUpdate) onDataUpdate();
      // If we were editing this record, reset form
      if (formData.id === id) resetForm();
    } else {
      setError("Failed to delete record");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 bg-white ">
      {error && (
        <p className="mb-4 text-center text-red-600 font-semibold">{error}</p>
      )}

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-8"
      >
        <input
          type="number"
          name="year"
          placeholder="Year"
          min="1900"
          max="2100"
          value={formData.year}
          onChange={handleChange}
          required
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="month"
          placeholder="Month"
          min="1"
          max="12"
          value={formData.month}
          onChange={handleChange}
          required
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="india_stock"
          placeholder="India Stock"
          step="0.01"
          value={formData.india_stock}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="india_bond"
          placeholder="India bond"
          step="0.01"
          value={formData.india_bond}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="usa_stock"
          placeholder="USA Stock"
          step="0.01"
          value={formData.usa_stock}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="usa_fixed"
          placeholder="USA Fixed"
          step="0.01"
          value={formData.usa_fixed}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="india_pension"
          placeholder="India Pension"
          step="0.01"
          value={formData.india_pension}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="hsa"
          placeholder="HSA"
          step="0.01"
          value={formData.hsa}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="icici_prulife"
          placeholder="ICICI PruLife"
          step="0.01"
          value={formData.icici_prulife}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <input
          type="number"
          name="k_401k"
          placeholder="401k"
          step="0.01"
          value={formData.k_401k}
          onChange={handleChange}
          className="border rounded px-3 py-2 focus:outline-blue-500"
        />

        <div className="flex col-span-full gap-4">
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition flex-1"
          >
            {formData.id ? "Update MonthlyCorp" : "Add MonthlyCorp"}
          </button>

          {formData.id && (
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-400 text-white px-6 py-2 rounded hover:bg-gray-500 transition flex-1"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <p className="text-center text-gray-600">Loading records...</p>
      ) : (
        <div style={{ overflowX: "hidden" }}>
          <table className="min-w-full border border-gray-300 rounded-md text-left text-sm table-fixed">
            <colgroup>
              <col style={{ width: "6%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead className="bg-blue-100 text-blue-700 font-semibold">
              <tr>
                <th className="p-3 border border-gray-300">Year</th>
                <th className="p-3 border border-gray-300">Month</th>
                <th className="p-3 border border-gray-300">India Stock</th>
                <th className="p-3 border border-gray-300">India bond</th>
                <th className="p-3 border border-gray-300">USA Stock</th>
                <th className="p-3 border border-gray-300">USA Fixed</th>
                <th className="p-3 border border-gray-300">India Pension</th>
                <th className="p-3 border border-gray-300">HSA</th>
                <th className="p-3 border border-gray-300">ICICI PruLife</th>
                <th className="p-3 border border-gray-300">401k</th>
                <th className="p-3 border border-gray-300 text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {monthlyCorps.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="p-4 text-center text-gray-500 italic"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                monthlyCorps.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-100">
                    <td className="p-3 border border-gray-300">
                      {record.year}
                    </td>
                    <td className="p-3 border border-gray-300">
                      {new Date(record.year, record.month - 1).toLocaleString(
                        "default",
                        { month: "short" },
                      )}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatINR(parseFloat(record.india_stock).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatINR(parseFloat(record.india_bond).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatUSD(parseFloat(record.usa_stock).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatUSD(parseFloat(record.usa_fixed).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatINR(parseFloat(record.india_pension).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatUSD(parseFloat(record.hsa).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatINR(parseFloat(record.icici_prulife).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right">
                      {formatUSD(parseFloat(record.k_401k).toFixed(2))}
                    </td>
                    <td className="p-3 border border-gray-300 text-center space-x-2">
                      <button
                        onClick={() => handleEdit(record)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit record"
                      >
                        &#9998;
                      </button>
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete record"
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MonthlyCorpsForm;
